import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useOpsCustoms } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, LockKeyhole, Sparkles, Loader2, ShieldAlert } from "lucide-react";

const STATUS_LABEL = {
  NOT_STARTED: "Not started", IN_PREPARATION: "In preparation", SUBMITTED: "Submitted — awaiting release",
  UNDER_INSPECTION: "Under inspection", HOLD: "Customs hold", RELEASED: "Released",
};
const STATUS_COLOR = {
  NOT_STARTED: "bg-slate-100 text-slate-600", IN_PREPARATION: "bg-amber-50 text-amber-700",
  SUBMITTED: "bg-indigo-50 text-indigo-700", UNDER_INSPECTION: "bg-amber-50 text-amber-700",
  HOLD: "bg-red-50 text-red-700", RELEASED: "bg-emerald-50 text-emerald-700",
};

export default function CustomsPanel({ job }) {
  const qc = useQueryClient();
  const enabled = ["IN_PROGRESS", "DEPARTED", "ARRIVED", "CLEARANCE", "DELIVERY", "DELIVERED", "READY_FOR_EXECUTION"].includes(job.status);
  const { data, isLoading } = useOpsCustoms(job.id, enabled);
  const [busy, setBusy] = useState(false);
  const [declarationReference, setDeclarationReference] = useState("");
  const [releaseReference, setReleaseReference] = useState("");
  const [hsInputs, setHsInputs] = useState({});

  const refresh = () => qc.invalidateQueries({ queryKey: ["ops-customs", job.id] });
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); await refresh(); toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Customs action could not be completed"); }
    finally { setBusy(false); }
  };

  if (!enabled) return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600 flex gap-3">
      <LockKeyhole className="h-5 w-5 text-slate-400 shrink-0" />
      <div><div className="font-semibold text-slate-800">Customs is not available yet</div><div className="mt-0.5">Start shipment execution before opening a customs declaration.</div></div>
    </div>
  );
  if (isLoading) return <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">Loading customs record…</div>;

  const status = data?.status || "NOT_STARTED";

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="customs-panel">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div className="text-sm font-semibold text-slate-900">Customs declaration</div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_COLOR[status]}`}>{STATUS_LABEL[status]}</span>
      </div>
      <div className="p-5 space-y-4">
        {status === "NOT_STARTED" && (
          <Button onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/start`, {}), "Customs declaration prepared")} disabled={busy} data-testid="customs-start">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Start customs preparation
          </Button>
        )}

        {status === "IN_PREPARATION" && (data?.hsLines || []).map((line) => (
          <div key={line.id} className="rounded-lg border border-slate-200 p-3">
            <div className="text-sm font-medium text-slate-800">{line.description}</div>
            {line.hsCode ? (
              <div className="mt-1 text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />HS {line.hsCode} · Duty {line.dutyAmount} · VAT {line.vatAmount} (set by {line.setBy})</div>
            ) : (
              <div className="mt-2 space-y-2">
                {line.aiSuggestedHsCode ? (
                  <div className="text-xs text-indigo-700 bg-indigo-50 rounded px-2 py-1">AI-suggested: {line.aiSuggestedHsCode} — {line.aiSuggestedNote}. A human must confirm before this becomes authoritative.</div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/lines/${line.id}/suggest-hs-code`), "AI suggestion added — human confirmation still required")} disabled={busy}>
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />Suggest HS code (AI, non-authoritative)
                  </Button>
                )}
                <div className="grid gap-2 md:grid-cols-[140px_120px_120px_auto]">
                  <Input placeholder="HS code*" value={hsInputs[line.id]?.hsCode || ""} onChange={(e) => setHsInputs((s) => ({ ...s, [line.id]: { ...s[line.id], hsCode: e.target.value } }))} data-testid={`hs-code-${line.id}`} />
                  <Input placeholder="Duty AED" type="number" value={hsInputs[line.id]?.dutyAmount || ""} onChange={(e) => setHsInputs((s) => ({ ...s, [line.id]: { ...s[line.id], dutyAmount: e.target.value } }))} />
                  <Input placeholder="VAT AED" type="number" value={hsInputs[line.id]?.vatAmount || ""} onChange={(e) => setHsInputs((s) => ({ ...s, [line.id]: { ...s[line.id], vatAmount: e.target.value } }))} />
                  <Button size="sm" onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/lines/${line.id}/hs-code`, hsInputs[line.id] || {}), "HS code confirmed by human review")} disabled={busy || !hsInputs[line.id]?.hsCode}>Confirm</Button>
                </div>
              </div>
            )}
          </div>
        ))}

        {status === "IN_PREPARATION" && (data?.hsLines || []).every((l) => l.hsCode) && (
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3.5">
            <div className="text-xs font-semibold text-indigo-900 mb-2">Submit to Customs (Manual / External Customs System)</div>
            <div className="flex gap-2">
              <Input placeholder="Declaration reference*" value={declarationReference} onChange={(e) => setDeclarationReference(e.target.value)} data-testid="customs-declaration-ref" />
              <Button onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/submit`, { declarationReference }), "Declaration submitted")} disabled={busy || !declarationReference.trim()} data-testid="customs-submit">Submit</Button>
            </div>
          </div>
        )}

        {status === "HOLD" && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3.5 flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-800">Customs hold — {data?.holdReason}</div>
              <Button size="sm" className="mt-2" onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/inspection`, { result: "CLEARED" }), "Inspection cleared")} disabled={busy}>Record inspection cleared</Button>
            </div>
          </div>
        )}

        {(status === "SUBMITTED" || status === "UNDER_INSPECTION") && (
          <div className="flex flex-wrap gap-2">
            {status === "SUBMITTED" && (
              <Button size="sm" variant="outline" onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/inspection`, { result: "HOLD", reason: "Selected for physical inspection" }), "Inspection hold recorded")} disabled={busy}>
                <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />Record inspection hold
              </Button>
            )}
            <Input placeholder="Release reference*" value={releaseReference} onChange={(e) => setReleaseReference(e.target.value)} className="w-56" />
            <Button size="sm" onClick={() => run(() => api.post(`/ops/jobs/${job.id}/customs/release`, { releaseReference }), "Customs released")} disabled={busy || !releaseReference.trim()} data-testid="customs-release">Release</Button>
          </div>
        )}

        {status === "RELEASED" && (
          <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />Released {data?.releaseReference} on {data?.releasedAt ? new Date(data.releasedAt).toLocaleString() : ""} · Declaration {data?.declarationReference}
          </div>
        )}
      </div>
    </div>
  );
}
